'use client';

import { MenuRounded } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { radius } from '@odys/design-system/tokens/radius';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { useEffect, useLayoutEffect, useState } from 'react';

import type { OwnedDmcLink } from '@/app/api/lib/dmcs/owned-dmc-links';
import type { Role } from '@/app/api/lib/permissions/matrix';
import { IMPERSONATION_BANNER_HEIGHT_VAR } from '@/components/auth/impersonation-banner-height';
import { signInHref } from '@/components/sign-in/sign-in-href';
import { withLocalePrefix } from '@/configurations/i18n/localePath';
import { Link, usePathname } from '@/configurations/i18n/navigation';
import { trackNavigationClick } from '@/configurations/tracking/navigation';

import { CartMenuButton } from './CartMenuButton';
import { CurrencySwitcher } from './CurrencySwitcher';
import { DestinationsMenuButton } from './DestinationsMenuButton';
import { LocaleSwitcher } from './LocaleSwitcher';
import { MobileNavDrawer } from './MobileNavDrawer';
import { MyCatalogMenuButton } from './MyCatalogMenuButton';
import { navItemFor, splitCatalogMenu, visibleNavLinks } from './nav-links';
import { UserMenu } from './UserMenu';

type NavbarProps = {
    /** Initiales du user courant. Absent en mode visitor → avatar masqué. */
    initials?: string;
    /** Email du user courant. Présent uniquement en session authentifiée. */
    email?: string;
    /** Nom d'affichage du user courant (ex : "Victor Legue"). Optionnel. */
    displayName?: string;
    /**
     * Organisation du user courant — son agence (`role === 'agency'`) ou sa DMC
     * (`role === 'dmc'`). Affichée dans l'en-tête du menu avatar. Absente pour
     * un Visitor et pour un compte dont la donnée manque côté source.
     */
    organizationName?: string;
    /** Rôle du viewer. Sert à câbler les liens de navigation visibles selon le rôle. */
    role?: Role;
    /**
     * Fiches possédées par le DMC connecté, chacune avec son lien catalogue et son deep-link
     * Odys (cf. `ownedDmcLinks`). Câble le(s) lien(s) « Mon catalogue » de la barre et l'item
     * « Aller sur Odys » du menu avatar. Vide pour Agency/Visitor, et pour un DMC sans fiche
     * résolvable (= liens retirés, défense-en-profondeur).
     */
    dmcLinks?: readonly OwnedDmcLink[];
    /**
     * Compte en attente de validation (`role === 'visitor'` +
     * `accountStatus === 'pending'`). Relayé au `UserMenu` pour afficher
     * l'indicateur sablier + l'encart explicatif, sans bannière pleine largeur.
     */
    isPending?: boolean;
};

type NavbarMode = 'overlay' | 'docked';

type NavLinkProps = {
    href: string;
    isActive: boolean;
    isOverlay: boolean;
    onClick: () => void;
    children: React.ReactNode;
};

function NavLink({ href, isActive, isOverlay, onClick, children }: NavLinkProps) {
    return (
        <Typography
            component={Link}
            href={href}
            onClick={onClick}
            sx={{
                fontWeight: 400,
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                color: (theme) =>
                    isOverlay
                        ? alpha(theme.palette.common.white, isActive ? 0.95 : 0.8)
                        : isActive
                          ? theme.palette.text.primary
                          : theme.palette.text.secondary,
                transition: 'color 200ms ease',
                '&:hover': {
                    color: isOverlay ? 'common.white' : 'text.primary',
                },
            }}
        >
            {children}
        </Typography>
    );
}

/**
 * Sélecteur du sentinel DOM placé par les composants Hero plein-écran
 * (HomepageHero, DestinationHero). Sa visibilité dans le viewport
 * détermine le mode `overlay` (glass sombre + texte blanc) vs `docked`
 * (glass clair + texte foncé) de la navbar.
 */
const HERO_SENTINEL_SELECTOR = '[data-navbar-hero-sentinel]';

export function Navbar({
    initials,
    email,
    displayName,
    organizationName,
    role,
    dmcLinks = [],
    isPending,
}: NavbarProps = {}) {
    const t = useTranslations('nav');
    const locale = useLocale();
    // `usePathname` de next-intl renvoie le chemin SANS préfixe : c'est ce qu'attendent les
    // comparaisons `pathname === href` ci-dessous, dont les hrefs sont déclarés nus.
    const pathname = usePathname();
    const mode = useNavbarMode(pathname);

    const isOverlay = mode === 'overlay';
    // Tone for sub-components that still use 'overlay' | 'solid' vocabulary.
    const tone = isOverlay ? ('overlay' as const) : ('solid' as const);
    // Le toggle devise n'a de sens que pour un user authentifié (Agency/DMC) ;
    // masqué pour les Visitors, y compris ceux en attente de validation (#2147).
    const canSwitchCurrency = role === 'agency' || role === 'dmc';
    const { before: preLinks, after: afterLinks } = visibleNavLinks(role, dmcLinks);
    // Plusieurs fiches → un seul bouton « Mon catalogue » déroulant, plutôt qu'un lien par fiche.
    // Le drawer mobile garde `afterLinks` à plat : un menu dans un menu n'apporte rien.
    const { links: postLinks, catalogMenu } = splitCatalogMenu(afterLinks);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    // Toute navigation referme le drawer (liens internes, overlay Destinations…).
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [pathname]);

    return (
        <Box
            component="header"
            sx={{
                position: 'fixed',
                // La bannière d'impersonation est `position: fixed` en haut : sans décaler la navbar
                // (elle aussi fixée), elle la recouvrirait. La variable n'existe que quand la
                // bannière s'affiche, d'où le fallback à 0px.
                top: isOverlay
                    ? `calc(16px + var(${IMPERSONATION_BANNER_HEIGHT_VAR}, 0px))`
                    : `var(${IMPERSONATION_BANNER_HEIGHT_VAR}, 0px)`,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: (theme) => theme.zIndex.appBar,
                borderRadius: isOverlay ? radius.lg : 0,
                px: 0,
                py: 0,
                width: isOverlay ? 'calc(100% - 32px)' : '100%',
                maxWidth: isOverlay ? 900 : 10000,
                transition: [
                    'top 600ms ease-in-out',
                    'width 600ms ease-in-out',
                    'max-width 600ms ease-in-out',
                    'border-radius 600ms ease-in-out',
                    'background-color 400ms ease',
                    'border-bottom-color 400ms ease',
                ].join(', '),
                backgroundColor: (theme) =>
                    isOverlay
                        ? alpha(theme.palette.common.black, 0.1)
                        : theme.palette.background.default,
                backdropFilter: isOverlay ? 'blur(20px)' : 'none',
                WebkitBackdropFilter: isOverlay ? 'blur(20px)' : 'none',
                borderBottom: '1px solid',
                borderBottomColor: (theme) =>
                    isOverlay ? 'transparent' : alpha(theme.palette.common.black, 0.08),
            }}
        >
            <Box
                sx={{
                    maxWidth: 1200,
                    mx: 'auto',
                    px: isOverlay ? 1.25 : 3,
                    py: 1,
                    width: '100%',
                }}
            >
                <Stack
                    direction="row"
                    spacing={{ xs: 1, md: 5 }}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Box
                        component={Link}
                        href="/"
                        onClick={() =>
                            trackNavigationClick({ navItem: 'logo', triggerContext: 'navbar' })
                        }
                        sx={{ display: 'flex', alignItems: 'center', px: 0.5 }}
                        aria-label="Odys"
                    >
                        <Image
                            src={isOverlay ? '/odyss-logo-white.png' : '/odyss-logo.png'}
                            alt="Odys"
                            width={80}
                            height={24}
                            priority
                            style={{ display: 'block' }}
                        />
                    </Box>

                    <Stack
                        direction="row"
                        spacing={2.5}
                        sx={{
                            alignItems: 'center',
                            ml: 2,
                            display: { xs: 'none', md: 'flex' },
                        }}
                    >
                        {preLinks.map(({ labelKey, href, label }) => (
                            <NavLink
                                key={href}
                                href={href}
                                isActive={pathname === href}
                                isOverlay={isOverlay}
                                onClick={() =>
                                    trackNavigationClick({
                                        navItem: navItemFor(labelKey),
                                        triggerContext: 'navbar',
                                    })
                                }
                            >
                                {label ?? t(labelKey)}
                            </NavLink>
                        ))}
                        <DestinationsMenuButton isOverlay={isOverlay} triggerContext="navbar" />
                        {postLinks.map(({ labelKey, href, label }) => (
                            <NavLink
                                key={href}
                                href={href}
                                isActive={pathname === href}
                                isOverlay={isOverlay}
                                onClick={() =>
                                    trackNavigationClick({
                                        navItem: navItemFor(labelKey),
                                        triggerContext: 'navbar',
                                    })
                                }
                            >
                                {label ?? t(labelKey)}
                            </NavLink>
                        ))}
                        {catalogMenu.length > 0 ? (
                            <MyCatalogMenuButton
                                isOverlay={isOverlay}
                                entries={catalogMenu.map(({ label, href }) => ({
                                    name: label ?? href,
                                    href,
                                }))}
                            />
                        ) : null}
                    </Stack>

                    <Box sx={{ flex: 1 }} />

                    {initials && email ? (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            {role === 'agency' ? <CartMenuButton isOverlay={isOverlay} /> : null}
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{
                                    alignItems: 'center',
                                    display: { xs: 'none', md: 'flex' },
                                }}
                            >
                                {canSwitchCurrency && (
                                    <CurrencySwitcher tone={tone} triggerContext="navbar" />
                                )}
                                <LocaleSwitcher tone={tone} triggerContext="navbar" />
                            </Stack>
                            <UserMenu
                                initials={initials}
                                email={email}
                                displayName={displayName}
                                organizationName={organizationName}
                                role={role ?? 'visitor'}
                                dmcLinks={dmcLinks}
                                isPending={isPending}
                            />
                        </Stack>
                    ) : (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                alignItems: 'center',
                                flexShrink: 0,
                                display: { xs: 'none', md: 'flex' },
                            }}
                        >
                            <LocaleSwitcher tone={tone} triggerContext="navbar" />
                            <Button
                                component={Link}
                                // Le return-to porte la locale, sinon le retour après login
                                // repasse par la négociation et peut changer de langue.
                                href={signInHref(withLocalePrefix(locale, pathname))}
                                variant="text"
                                sx={{
                                    whiteSpace: 'nowrap',
                                    color: isOverlay ? 'common.white' : 'primary.main',
                                    '&:hover': {
                                        backgroundColor: (theme) =>
                                            isOverlay
                                                ? alpha(theme.palette.common.white, 0.12)
                                                : undefined,
                                    },
                                }}
                            >
                                {t('signIn')}
                            </Button>
                            <Button
                                component={Link}
                                href="/sign-up"
                                variant="contained"
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                {t('signUp')}
                            </Button>
                        </Stack>
                    )}

                    <IconButton
                        onClick={() => setIsMobileMenuOpen(true)}
                        aria-label={t('menu')}
                        sx={{
                            display: { xs: 'inline-flex', md: 'none' },
                            color: isOverlay ? 'common.white' : 'text.primary',
                        }}
                    >
                        <MenuRounded />
                    </IconButton>
                </Stack>

                <MobileNavDrawer
                    open={isMobileMenuOpen}
                    onClose={() => setIsMobileMenuOpen(false)}
                    preLinks={preLinks}
                    postLinks={afterLinks}
                    isAuthenticated={Boolean(initials && email)}
                    canSwitchCurrency={canSwitchCurrency}
                />
            </Box>
        </Box>
    );
}

/**
 * Mode de la navbar selon la présence et la visibilité d'un sentinel DOM
 * (`data-navbar-hero-sentinel`). Pages sans sentinel → toujours `docked`.
 * Pages avec sentinel → `overlay` tant que ≥30 % du sentinel est visible,
 * `docked` en dessous (transition déclenchée avant la sortie complète du hero).
 *
 * Un MutationObserver de fallback guette l'apparition tardive du sentinel
 * pour les pages dont le hero se monte après un fetch asynchrone (ex : pages
 * destination). Reset à chaque navigation (`pathname`).
 *
 * La détection tourne dans un `useLayoutEffect` (et non `useEffect`) : sur une
 * page dont le hero est présent au montage (ex : homepage), le mode correct est
 * `overlay`, mais l'état initial est `docked`. Un `useEffect` corrigerait le mode
 * APRÈS le premier paint → la navbar pleine largeur s'affiche puis s'anime vers
 * sa forme flottante (transition 600ms) = l'effet de « flash ». En basculant
 * avant le paint, la première frame peinte est déjà la bonne forme (et une
 * transition CSS ne se déclenche jamais sur le paint initial).
 */
function useNavbarMode(pathname: string): NavbarMode {
    const [mode, setMode] = useState<NavbarMode>('docked');

    useLayoutEffect(() => {
        let intersectionObserver: IntersectionObserver | null = null;

        function attachIntersectionObserver(sentinel: Element) {
            setMode('overlay');
            intersectionObserver = new IntersectionObserver(
                ([entry]) => {
                    if (!entry) return;
                    setMode(entry.isIntersecting ? 'overlay' : 'docked');
                },
                { threshold: 0.3 },
            );
            intersectionObserver.observe(sentinel);
        }

        const sentinel = document.querySelector(HERO_SENTINEL_SELECTOR);
        if (sentinel) {
            attachIntersectionObserver(sentinel);
            return () => intersectionObserver?.disconnect();
        }

        // Sentinel absent au montage (hero chargé de façon asynchrone, ex : pages
        // destination qui attendent un fetch DB). On guette son apparition.
        setMode('docked');
        const mutationObserver = new MutationObserver(() => {
            const lateSentinel = document.querySelector(HERO_SENTINEL_SELECTOR);
            if (!lateSentinel) return;
            mutationObserver.disconnect();
            attachIntersectionObserver(lateSentinel);
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });

        return () => {
            mutationObserver.disconnect();
            intersectionObserver?.disconnect();
        };
    }, [pathname]);

    return mode;
}
